import React, { useState, useRef } from 'react';
import { useFormik } from 'formik';
import { makeStyles } from '@material-ui/core';
import { contactUsValidationSchema } from './contactUsValidationSchema';
import { BaseTypography, BaseButton, FormikField } from 'src/components/common';
import { IContactUsForm } from 'src/api/base/ContactUs/types';
import { IValidationErrors } from 'src/api/base/baseApiTypes';
import { useFormikErrorsHandler } from 'src/utils/hooks/useFormikErrorsHandler';
import ReCAPTCHA from "react-google-recaptcha";

const useStyles = makeStyles(() => ({
  fieldsGroup: {
    marginTop: '8px',
    display: 'grid',
    gridAutoFlow: 'column',
    gridGap: '20px',
  },
  mainField: {
    display: 'grid',
  },
  submitBtn: {
    marginTop: '25px',
  },
}));

export type ContactUsFormProps = {
  onSubmit: (data: IContactUsForm) => void;
  loading?: boolean;
  isVertical?: boolean;
  errors?: IValidationErrors;
  title?: string | React.ElementType;
  startTabIndex?: number;
};

export const ContactUsForm: React.FC<ContactUsFormProps> = (props) => {
	const reCaptchaRef = useRef();
	const [captchaIsDone, setCaptchaDone] = useState(false);
  const {
    onSubmit,
    isVertical = true,
    loading,
    errors,
    title,
    startTabIndex = 1,
  } = props;
  const classes = useStyles();
  const formik = useFormik({
    initialValues: {
      name: '',
      email: '',
      question: '',
    },
    validationSchema: contactUsValidationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: (values) => {
      if(captchaIsDone) {
			onSubmit(values);
			formik.setFieldValue('name', '');
			formik.setFieldValue('email', '');
			formik.setFieldValue('question', '');
			reCaptchaRef.current.reset();
		}else {
			alert('Please verify the reCAPTCHA!');
		}
      formik.setErrors({});
    },
  });
  
  function onChange(value) {
	  setCaptchaDone(true);
  }

  const { setFieldError } = formik;

  useFormikErrorsHandler({ errors, setFieldError });

  const messageField = (
    <FormikField
      label="Message"
      placeholder="For questions about this site, message the administrator here"
      multiline
      rowsMin={6}
      rows={6}
      name="question"
      formik={formik}
      style={{ marginBottom: '10px' }}
      InputProps={{
        style: {
          height: '100%',
        },
      }}
      inputInnerProps={{
        tabIndex: startTabIndex + 2,
      }}
    />
  );
  
  const recaptchaField = (
		<ReCAPTCHA
			sitekey="6LfINREqAAAAALkP8OIgxT_TsRHLhswSZ5ykecUA"
			onChange={onChange}
			ref={reCaptchaRef}
	    />
  );

  return (
    <div id="contactUs">
      <BaseTypography variant="h3">
        {title || (
          <>
            <span>CONTACT </span>
            <span className="primary">US</span>
          </>
        )}
      </BaseTypography>
      <form onSubmit={formik.handleSubmit} className={classes.fieldsGroup}>
        <div className={classes.mainField}>
          <FormikField
            label="Name"
            placeholder="Your name"
            name="name"
            formik={formik}
            inputInnerProps={{
              tabIndex: startTabIndex,
            }}
          />
          <FormikField
            label="Email"
            placeholder="example@mail.com"
            name="email"
            formik={formik}
            style={{ margin: '10px 0' }}
            inputInnerProps={{
              tabIndex: startTabIndex + 1,
            }}
          />

          {isVertical && messageField}
		  {recaptchaField} 
          <BaseButton
            id='contactUsBtn'
            tabIndex={startTabIndex + 3}
            className={classes.submitBtn}
            loading={loading}
            color="primary"
            variant="contained"
            size="large"
            type="submit">
            Send
          </BaseButton>
        </div>
        {!isVertical && messageField}
      </form>
    </div>
  );
};
